import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  createGooglePayButton,
  createGooglePayDeferredButton,
} from "@/api/google-pay/api";
import type { GooglePayPaymentData } from "@/api/google-pay";
import {
  isInShadowDom,
  PaymentButtonSlotIds,
  PaymentButtonSlots,
} from "@/utils/shadow-dom";
import { useIsMobile } from "@/hooks/useIsMobile";

export type GooglePayMerchantConfigs = {
  env: string;
  merchantId: string;
  merchantSiteId: string;
};

type GooglePayButtonPropsBase = {
  /** Transaction amount. */
  amount: string;
  /** Transaction currency code. */
  currencyCode: string;
  /** BCP 47 locale for the button (e.g. "en", "ar"). Re-renders button when changed. */
  locale?: string;
  onError?: (error: Error) => void;
  containerId?: string;
  /** When true, button is non-interactive and visually dimmed (e.g. payment in progress). */
  disabled?: boolean;
};

type GooglePayButtonPropsWithConfig = GooglePayButtonPropsBase & {
  /** When provided, uses deferred flow: on click runs start-transaction then shows GP sheet. */
  onDeferredClick?: never;
  /** Merchant config from API (e.g. startTransaction response). Required when not using onDeferredClick. */
  merchantConfigs: GooglePayMerchantConfigs;
  /** Called with full payment data when the user completes the Google Pay sheet. */
  onSuccess: (paymentData: GooglePayPaymentData) => void;
  merchantName?: string;
};

type GooglePayButtonPropsDeferred = GooglePayButtonPropsBase & {
  /** When provided, uses deferred flow: on click runs start-transaction then shows GP sheet. */
  onDeferredClick: () => Promise<void>;
  merchantConfigs?: never;
  onSuccess?: never;
  merchantName?: never;
};

type GooglePayButtonProps =
  | GooglePayButtonPropsWithConfig
  | GooglePayButtonPropsDeferred;

export const GooglePayButton = ({
  amount,
  currencyCode,
  merchantName = "Merchant",
  locale,
  onSuccess,
  onDeferredClick,
  merchantConfigs,
  onError,
  containerId = "google-pay-button-container",
  disabled = false,
}: GooglePayButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const dummyRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Same pattern as NuveiNewCreditCardWidget: when in shadow DOM, look up light-DOM slot container by id.
  useEffect(() => {
    if (dummyRef.current && isInShadowDom(dummyRef.current)) {
      const el = document.getElementById(PaymentButtonSlotIds.googlePay);
      setPortalTarget(el as HTMLElement | null);
    }
  }, []);

  const handleDeferredClick = useCallback(() => {
    if (onDeferredClick) {
      onDeferredClick().catch((err) =>
        onError?.(err instanceof Error ? err : new Error(String(err)))
      );
    }
  }, [onDeferredClick, onError]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const setupButton = onDeferredClick
      ? createGooglePayDeferredButton(handleDeferredClick, locale)
      : merchantConfigs &&
        createGooglePayButton({
          merchantConfig: {
            env: "TEST",
            merchant_id: merchantConfigs.merchantId,
            merchant_site_id: merchantConfigs.merchantSiteId,
            merchantName,
          },
          transactionInfo: {
            totalPrice: Number.parseFloat(amount).toFixed(2),
            totalPriceStatus: "FINAL" as const,
            currencyCode,
          },
          locale,
          onPaymentData: (paymentData) => {
            onSuccess?.(paymentData);
          },
          onError: (error) => {
            onError?.(
              error instanceof Error ? error : new Error(String(error))
            );
          },
        });

    if (!setupButton) {
      return;
    }

    setupButton
      .then((button) => {
        if (container && button) {
          button.style.height = isMobile ? "49px" : "56px";
          container.innerHTML = "";
          container.appendChild(button);
        } else if (!button) {
          onError?.(new Error("Google Pay is not available"));
        }
      })
      .catch((error) => {
        onError?.(error instanceof Error ? error : new Error(String(error)));
      });

    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [
    onDeferredClick,
    handleDeferredClick,
    merchantConfigs,
    merchantName,
    amount,
    currencyCode,
    locale,
    onSuccess,
    onError,
    isMobile,
  ]);

  const buttonContainer = (
    <div
      id={containerId}
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        ...(disabled && {
          pointerEvents: "none",
          opacity: 0.6,
        }),
      }}
    />
  );

  // In shadow DOM: portal button into light-DOM slot container; slot projects it here (same pattern as NuveiNewCreditCardWidget).
  if (portalTarget) {
    return (
      <>
        <div ref={dummyRef} style={{ display: "none" }} />
        {createElement("slot", { name: PaymentButtonSlots.googlePay })}
        {createPortal(buttonContainer, portalTarget)}
      </>
    );
  }

  return (
    <>
      <div ref={dummyRef} style={{ display: "none" }} />
      {buttonContainer}
    </>
  );
};
